import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import StyledLoadingButton from "../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { MITIGATED_RISKS } from "../models/common/events.constants";
import { IRecentEventsTableRow } from "../models/common/events.models";

interface IFeedbackForm {
  risks: Record<string, boolean>;
}

interface IFeedbackFormProps {
  submitLoading?: boolean;
  rowData: IRecentEventsTableRow;
  onCancel: () => void;
  onSubmitClick: (
    params: IRecentEventsTableRow,
    mitigatedRisk: string[]
  ) => void;
}

const FeedbackForm = ({
  rowData,
  onCancel,
  onSubmitClick,
  submitLoading,
}: IFeedbackFormProps): JSX.Element => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IFeedbackForm>({
    defaultValues: {
      risks: MITIGATED_RISKS.reduce((acc, risk) => {
        const mitigatedRisk = rowData.mitigatedRisk?.find(r => r === risk);

        acc[risk] = Boolean(mitigatedRisk) ?? false;

        return acc;
      }, {} as Record<string, boolean>),
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IFeedbackForm> = data => {
    const selectedItems = Object.keys(data.risks).filter(
      key => data.risks[key]
    );

    onSubmitClick(rowData, selectedItems);
  };

  const selectedRisks = watch("risks");

  const isAtLeastOneSelected = Object.values(selectedRisks).some(Boolean);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        {MITIGATED_RISKS.map(risk => (
          <Controller
            key={risk}
            name={`risks.${risk}`}
            control={control}
            rules={{
              validate: () =>
                isAtLeastOneSelected || "Please select at least one option.",
            }}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={risk}
              />
            )}
          />
        ))}
      </FormGroup>
      {errors.risks && !isAtLeastOneSelected && (
        <FormHelperText error sx={{ fontSize: "0.75em" }}>
          Please select at least one option.
        </FormHelperText>
      )}
      <Stack
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={2}
        sx={{ paddingTop: 3 }}
      >
        <StyledLoadingButton
          startIcon={null}
          loadingPosition="start"
          variant="outlined"
          color="inherit"
          onClick={onCancel}
        >
          Cancel
        </StyledLoadingButton>
        <StyledLoadingButton
          type="submit"
          loading={submitLoading}
          startIcon={null}
          loadingPosition="start"
          variant="contained"
          color="primary"
        >
          Apply
        </StyledLoadingButton>
      </Stack>
    </form>
  );
};

export default FeedbackForm;
